import { Component, Inject, Input, OnInit } from 'angular-ts-decorators';

import { fetchGroupPrices } from "@gn-audio/core/api/pricing/fetch-group-prices";
import { IFromPrice, IPrice, ISFCCGroupPrice, ISFCCProductPrice } from '@gn-audio/core/api/pricing/models';
import { fetchSiteFeatures, ISiteFeature, SiteFeature } from '@gn-audio/core/api/site-settings';
import { IProductWithAttributes } from '@gn-audio/core/api/product-catalogue/models/product-with-attributes';
import { fetchEcomSetting } from '@gn-audio/core/api/site-settings/fetch-ecom-setting';
import { IEcomSetting } from '@gn-audio/core/api/site-settings/models/ecom-setting';
import { IProductService } from '@gn-audio/sfcc-basket/models/sfcc-product-service';

import { CustomizerEvents } from '../models/customizer-events';
import { fetchFamilyOrGroupsAttributes, sortGroupsByPrice } from '../utils';
import { notifySubscribers } from '../utils/notify-subscribers';
import { CustomizerV3ApiService } from './customizer-v3-api.service';
import { IHeroCustomizerAccessory, IHeroCustomizerGiftWrapping } from './models';
import { IHeroCustomizerV3Configuration } from './models/hero-customizer-v3.configuration';

import template from './hero-customizer-v3.component.html';

@Component({
  selector: 'heroCustomizerV3',
  template,
})
export class HeroCustomizerV3Component implements OnInit {
  public static $inject: ReadonlyArray<string> = [];

  @Input('@') public configurationId?: string;

  public configuration?: IHeroCustomizerV3Configuration;
  public groups?: IProductWithAttributes[];
  public selection?: IProductWithAttributes;
  public services?: IProductService[];
  public engravingMessage?: string;
  public engravingSku?: string;
  public giftWrappingMessage?: string;
  public giftWrappingSku?: string;
  public selectedServices: IHeroCustomizerGiftWrapping[] = [];
  public selectedServicesPrices?: (IPrice | ISFCCProductPrice)[];
  public selectedAccessories: IHeroCustomizerAccessory[] = [];
  public selectedAccessoriesPrices?: (IPrice | ISFCCProductPrice)[];
  public hideUniqueSellingPoint: boolean = false;
  public is3dRenderingEnabled: boolean = false;

  constructor(
    @Inject('$location') private readonly $location: ng.ILocationService,
    @Inject('$q') private readonly $q: ng.IQService,
    @Inject('customizerV3ApiService') private readonly customizerV3ApiService: CustomizerV3ApiService,
  ) { }

  public async ngOnInit() {
    this.customizerV3ApiService
      .getHeroCustomizerV3(this.configurationId)
      .then(this.onConfigurationLoad.bind(this));

    this.is3dRenderingEnabled = await this.is3dRenderingFeatureOn();
  }

  public onSelectionChange(group: IProductWithAttributes) {
    this.selection = group;
    this.selectedAccessories = [];
    this.selectedAccessoriesPrices = [];
    this.selectedServices = [];
    this.selectedAccessoriesPrices = [];
    notifySubscribers(CustomizerEvents.SelectionChanged, group);
    this.setSkuInUrl();
  }

  private onConfigurationLoad(configuration: IHeroCustomizerV3Configuration) {
    this.configuration = configuration;
    notifySubscribers(CustomizerEvents.ConfigurationLoaded, this.configuration);
    this.$q.when(fetchFamilyOrGroupsAttributes(this.configuration.familyId, this.configuration.groupIds))
      .then(this.onGroupsLoad.bind(this));
  }

  private onGroupsLoad(groups: IProductWithAttributes[] = []) {
    notifySubscribers(CustomizerEvents.GroupsLoaded, groups);
    if (this.configuration.keepGroupSortOrder && this.configuration.groupIds.length > 0) {
      this.onGroupsProcess(groups);
      return;
    }

    this.$q.when(fetchEcomSetting())
      .then((ecomSetting: IEcomSetting | null) => {
        if (!ecomSetting?.ecomEnabled) {
          this.onGroupsProcess(groups);
        } else {
          this.$q.when(fetchGroupPrices(groups.map((x: IProductWithAttributes) => x.groupId)))
            .then((prices: IFromPrice[] | ISFCCGroupPrice[]) => { this.onGroupsProcess(sortGroupsByPrice(groups, prices)); });
        }
      });
  }

  public onEngravingMessageChange(message: string) {
    this.engravingMessage = message;
  }

  public onGiftWrappingStatusChange(message: string, sku: string) {
    this.giftWrappingSku = sku;
    this.giftWrappingMessage = message;

    this.setServicesObject();
  }

  private setServicesObject() {
    this.services = [];
   
    if (!!this.giftWrappingSku && !!this.giftWrappingMessage) {       
      this.services.push({ sku: this.giftWrappingSku, value: this.giftWrappingMessage });
    }
  }

  public onSelectedAccessoryChange(selectedAccessories: IHeroCustomizerAccessory[]) {
    this.selectedAccessories = selectedAccessories;
    this.selectedAccessoriesPrices = selectedAccessories.map((accessory) => accessory.price);
  }

  public onSelectedServicesChange(selectedServices: IHeroCustomizerGiftWrapping[]) {
    this.selectedServices = selectedServices;
    this.selectedServicesPrices = selectedServices.map((service) => service.price);
  }

  private onGroupsProcess(groups: IProductWithAttributes[] = []) {
    this.groups = groups;
    notifySubscribers(CustomizerEvents.GroupsProcessed, this.groups);
    this.setSelectionOnInit();
  }

  private setSelectionOnInit() {
    const skuFromUrl = this.$location.hash() || '';
    if (skuFromUrl) {
      this.selection = this.groups.filter((x: IProductWithAttributes) => x.sku === skuFromUrl)[0];
    }

    if (!this.selection) {
      this.selection = this.groups[0];
      this.setSkuInUrl();
    }

    notifySubscribers(CustomizerEvents.SelectionChanged, this.selection);
  }

  private setSkuInUrl() {
    this.$location.hash(this.selection.sku);
  }

  public get selectedProductDisplayText(): string {
    return this.selection?.productName ? `${this.selection.productName} | ${this.selection.sku}` : `${this.selection?.sku || ''}`;
  }

  private async is3dRenderingFeatureOn(): Promise<boolean> {
    const features: ISiteFeature[] | null = await fetchSiteFeatures([
      SiteFeature.ProductVisualizationRendering,
    ]);

    return Array.isArray(features) && features.length > 0 && features[0].value;
  }
}
